import { useMemo } from "react";
import { CalendarRange, TrendingUp } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Badge } from "@/components/ui/badge";
import { fmtBRL, fmtInt, fmtPct } from "@/lib/format";
import { planPrice } from "@/lib/plans";

export default function ComparativoMensal({ clientes = [], plans = [], months = 6 }) {
  const data = useMemo(() => {
    // Agrupa por YYYY-MM
    const map = new Map();
    for (const c of clientes) {
      const key = (c.created_at || "").slice(0, 7); // YYYY-MM
      if (!key) continue;
      const acc = map.get(key) || { key, vendas: 0, receita: 0 };
      acc.vendas += 1;
      acc.receita += planPrice(c.plano_escolhido, plans);
      map.set(key, acc);
    }
    const arr = [...map.values()].sort((a, b) => a.key.localeCompare(b.key)).slice(-months);
    // Formata rótulo
    const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
    return arr.map((it) => {
      const [y, mm] = it.key.split("-");
      return { ...it, label: `${meses[parseInt(mm,10)-1] || mm}/${y.slice(-2)}` };
    });
  }, [clientes, plans, months]);

  // Cálculo de variação (último vs anterior)
  const last = data[data.length - 1];
  const prev = data[data.length - 2];
  const varReceita = last && prev && prev.receita > 0 ? ((last.receita - prev.receita) / prev.receita) * 100 : 0;
  const varVendas = last && prev && prev.vendas > 0 ? ((last.vendas - prev.vendas) / prev.vendas) * 100 : 0;

  const tone = (v) => (v > 0 ? "text-emerald-500" : v < 0 ? "text-red-500" : "text-muted-foreground");

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="comparativo-mensal">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Comparativo mensal</h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge variant="outline">
            Receita: <span className={`ml-1 font-semibold ${tone(varReceita)}`}>{varReceita > 0 ? "+" : ""}{fmtPct(varReceita)}</span>
          </Badge>
          <Badge variant="outline">
            Vendas: <span className={`ml-1 font-semibold ${tone(varVendas)}`}>{varVendas > 0 ? "+" : ""}{fmtPct(varVendas)}</span>
          </Badge>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-8">Sem dados suficientes para comparar.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Receita
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => fmtBRL(v)}
                  />
                  <Line type="monotone" dataKey="receita" name="Receita" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Vendas fechadas
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => fmtInt(v)}
                  />
                  <Bar dataKey="vendas" name="Vendas" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
